import { useCallback, useEffect, useRef, useState } from 'react';

// Level below which we consider the microphone silent.
const SILENCE_THRESHOLD = 0.02;
// Level above which we consider real audio detected (hysteresis gap avoids flapping).
const AUDIO_THRESHOLD = 0.04;
// During the 30-second startup check we require two *consecutive* audio samples
// before declaring the mic OK, so a single ambient spike doesn't pass the check.
const STARTUP_AUDIO_CHECKS_REQUIRED = 2;
// Once the warning is showing, choppy speech (word → breath → word) would keep
// resetting a "consecutive" counter between words, so recovery only requires
// any audio sample above the threshold.
const RECOVERY_AUDIO_CHECKS_REQUIRED = 1;
const SILENCE_DURATION_MS = 30_000;
const CHECK_INTERVAL_MS = 500;
// How long the success banner is shown before auto-dismissing.
const SUCCESS_BANNER_DURATION_MS = 3_000;

type Phase = 'idle' | 'startup' | 'monitoring' | 'warning';

interface UseNoAudioWarningResult {
  showWarning: boolean;
  showOverlay: boolean;
  showSuccess: boolean;
  openOverlay: () => void;
  dismissOverlay: () => void;
  dismissSuccess: () => void;
}

export function useNoAudioWarning(
  micLevel: number,
  isRecording: boolean,
  isPaused: boolean
): UseNoAudioWarningResult {
  const [showWarning, setShowWarning] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const micLevelRef = useRef(micLevel);
  const phaseRef = useRef<Phase>('idle');
  const audioChecksRef = useRef(0);
  const silenceSinceRef = useRef<number | null>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    micLevelRef.current = micLevel;
  }, [micLevel]);
  useEffect(() => {
    if (isRecording) return;

    phaseRef.current = 'idle';
    audioChecksRef.current = 0;
    silenceSinceRef.current = null;

    if (successTimerRef.current) clearTimeout(successTimerRef.current);
    successTimerRef.current = null;

    setShowWarning(false);
    setShowOverlay(false);
    setShowSuccess(false);
  }, [isRecording]);
  useEffect(() => {
    if (!isRecording || isPaused) {
      silenceSinceRef.current = null;
      return;
    }

    if (phaseRef.current === 'idle') {
      phaseRef.current = 'startup';
      audioChecksRef.current = 0;
      silenceSinceRef.current = null;
    }

    const id = setInterval(() => {
      const level = micLevelRef.current;
      const now = Date.now();
      const phase = phaseRef.current;

      switch (phase) {
        case 'startup': {
          if (level >= AUDIO_THRESHOLD) {
            audioChecksRef.current += 1;
            if (audioChecksRef.current >= STARTUP_AUDIO_CHECKS_REQUIRED) {
              phaseRef.current = 'monitoring';
              audioChecksRef.current = 0;
            }
          } else {
            audioChecksRef.current = 0;
            if (level < SILENCE_THRESHOLD) {
              silenceSinceRef.current ??= now;
              if (now - silenceSinceRef.current >= SILENCE_DURATION_MS) {
                phaseRef.current = 'warning';
                setShowWarning(true);
              }
            }
          }
          break;
        }

        case 'monitoring': {
          if (level < SILENCE_THRESHOLD) {
            silenceSinceRef.current ??= now;
            if (now - silenceSinceRef.current >= SILENCE_DURATION_MS) {
              phaseRef.current = 'warning';
              setShowWarning(true);
            }
          } else {
            silenceSinceRef.current = null;
          }
          break;
        }

        case 'warning': {
          if (level >= AUDIO_THRESHOLD) {
            audioChecksRef.current += 1;
            if (audioChecksRef.current >= RECOVERY_AUDIO_CHECKS_REQUIRED) {
              phaseRef.current = 'monitoring';
              audioChecksRef.current = 0;
              silenceSinceRef.current = null;
              setShowWarning(false);
              setShowOverlay(false);
              // Show success banner
              setShowSuccess(true);
              if (successTimerRef.current) clearTimeout(successTimerRef.current);
              successTimerRef.current = setTimeout(
                () => setShowSuccess(false),
                SUCCESS_BANNER_DURATION_MS
              );
            }
          }
          break;
        }
      }
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(id);
  }, [isRecording, isPaused]);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  const openOverlay = useCallback(() => setShowOverlay(true), []);
  const dismissOverlay = useCallback(() => setShowOverlay(false), []);
  const dismissSuccess = useCallback(() => {
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
    successTimerRef.current = null;
    setShowSuccess(false);
  }, []);

  return { showWarning, showOverlay, showSuccess, openOverlay, dismissOverlay, dismissSuccess };
}
